import { useEffect } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { getFirebaseAuth, isFirebaseConfigured } from '@shared/firebase';
import { useAuthStore } from '@shared/hooks/useAuthStore';

/**
 * T-89 — Mantiene viva la sessione del backoffice oltre le 24h SENZA allungare
 * l'access token.
 *
 * Si aggancia a `onIdTokenChanged` di Firebase, che scatta:
 *  - all'avvio, quando l'SDK ripristina la sessione dallo storage locale;
 *  - a ogni RINNOVO automatico dell'ID token (via refresh token, ~ogni ora);
 *  - al logout / revoca (user = null), anche da un'altra tab (Firebase sincronizza).
 *
 * Aggiorna il `token` nello store (usato dall'axios interceptor e da
 * ProtectedRoute) tenendolo sempre fresco, e fa logout pulito se Firebase perde
 * la sessione. È il pezzo che rende trasparente il refresh e allinea le tab.
 *
 * No-op in dev-mock / Firebase non configurato (token `dev:...`): lì il token
 * finto nello store non va toccato.
 */
export function useFirebaseAuthSync(): void {
  useEffect(() => {
    if (!isFirebaseConfigured() || import.meta.env.VITE_DEV_AUTH_MOCK === 'true') {
      return;
    }
    const unsubscribe = onIdTokenChanged(getFirebaseAuth(), async (user) => {
      if (!user) {
        // Sessione Firebase persa (logout/revoca): se eravamo loggati con un
        // token reale (non dev), logout pulito così ProtectedRoute rimanda al login.
        const current = useAuthStore.getState().token;
        if (current && !current.startsWith('dev:')) {
          useAuthStore.getState().logout();
        }
        return;
      }
      try {
        const fresh = await user.getIdToken();
        // Aggiorna SOLO il token; `user` (ruolo/tenant, da /users/me al login)
        // resta invariato e persistito.
        useAuthStore.setState({ token: fresh });
      } catch {
        // Rinnovo fallito: il prossimo 401 farà logout.
      }
    });
    return unsubscribe;
  }, []);
}
